// Redux Persist Migrations
//
// Kept in its own module (rather than inline in packs/index.js) so the
// migrations are unit-testable without booting the pack's side effects
// (Sentry.init, mixpanel.init, DOM mounting).
//
// Migration from version 1 to 2: Ensures company.token is set
// Migration from version 2 to 3: Drop persisted company slice — bootstrap re-injects it every load
// Migration from version 3 to 4: Drop the persisted `shoot` slice. It caches
//   the product catalog (shootProducts -> product_option_sets, including the
//   `selection_required` flag). When an admin edited a product option set, a
//   soft refresh rehydrated the stale catalog from localStorage and the
//   customer kept seeing the old "None" option / dropdown values. Dropping the
//   slice forces a fresh fetch via getShootData on the next dashboard / key
//   entry. See ENG-2067 QA.
// Migration from version 4 to 5: Products now carry per-pose `image_nodes`
//   instead of a single `image_type`, and the config flow derives pose slots
//   from them (ENG-2104). Product objects persisted before that change have no
//   `image_nodes`, which renders zero pose slots and lets an unconfigured
//   product pass selectInitialStepsComplete. Drop every slice that stores
//   product objects (`shoot`, `shootProduct`, `productConfig`) and reset the
//   in-flight config stack on `v3Order` — but keep the cart itself
//   (v3Order.orders / uuid) intact.
// Migration from version 5 to 6: Gallery assignment is now authorized by
//   gallery key, not by gallery id — the parent-facing endpoints require the
//   key and reject an id-only request. `additionalData` persisted before that
//   change holds a `picsGalleryId` with no `picsGalleryPassword` beside it, so
//   a rehydrated pre-deploy session would post the stale id without the key
//   and get rejected. This migration cannot recover the key; it makes the
//   stale identity inert so the flow falls back to key entry instead of acting
//   on half-populated context. The gallery-derived pose context is cleared
//   with it — the app already treats these fields as one unit (see the "Clear
//   stale gallery/pose data from previous orders" reset in
//   bnl_parent_dashboard).
// Migration from version 6 to 7: Optional-incentive declines are recorded on
//   `v3Order.optionalIncentiveProductIdsToIgnoreByOrder` (keyed by orderKey)
//   and `optionalCartLevelIncentiveProductIdsToIgnore`, and suppress the
//   redirect to the bonus config screen. Before the fix nothing removed an
//   entry when its order was emptied out of the cart, and because orderKey is
//   `<eventId>-<studentId>` it comes back identical when the parent starts
//   adding items again — so a decline persisted from an earlier session
//   silently suppressed the prompt forever. The reducer now forgets a decline
//   with its order; this migration cleans up the entries already sitting in
//   localStorage from before the fix. See ENG-2043 QA.
// Migration from version 7 to 8: cart-level optional-incentive declines are
//   now keyed by the benefit's SKU, not by a product id
//   (`optionalCartLevelIncentiveSkusToIgnore` replaces
//   `optionalCartLevelIncentiveProductIdsToIgnore`). The same benefit is a
//   separate row on every price list, so an id recorded against one school's
//   row never matched the row another school's list offers — the parent was
//   re-prompted for a bonus they had just declined (ENG-2043 QA). Persisted
//   ids cannot be translated (the catalogs they came from are not in the
//   store), so the old list is dropped and the new one seeded empty: at worst
//   the parent is offered a declined bonus once more.
// Migration from version 9 to 10: every retouching rule on the client (the
//   cart chip, the picker, the pre-cart offer) reads a pose's
//   `imageRecordType`, stamped from its product slot when the line is added. A
//   cart persisted before the stamp existed reads every photo as individual,
//   so a group photo already in it would be offered retouching that checkout
//   then refuses. Each unstamped pose on an ordinary line is stamped from the
//   line's own `imageNodes`, the same rule the add uses. A multi-config line
//   keeps no child image nodes, so its poses (top-level and per child) are
//   stamped from the child products in the persisted catalog
//   (`shoot.shootProducts`), found through the parent product or by the
//   child's own id; a child the catalog does not know is left as it is (the
//   server still refuses a group photo).

import {
  productNeedsBackgroundSelection,
  ProductLike,
} from '../components/v3/products/productConfigPredicates';
import { getImageRecords } from '../components/v3/products/imageRecords';

// A persisted slice is whatever shape it had when it was written to storage,
// which is not necessarily today's type — migrations exist precisely to bridge
// those historical shapes, so they are typed loosely on purpose.
type PersistedSlice = Record<string, unknown>;

interface PersistedRootState {
  company?: PersistedSlice & { name?: string; token?: string };
  shoot?: PersistedSlice;
  shootProduct?: PersistedSlice;
  productConfig?: PersistedSlice;
  v3Order?: PersistedSlice & { productConfig?: PersistedSlice };
  additionalData?: PersistedSlice;
  [slice: string]: unknown;
}

// redux-persist hands each migration the inbound persisted state, which is
// undefined when there is nothing stored yet.
type Migration = (
  state: PersistedRootState | undefined,
) => PersistedRootState | undefined;

// Stamps each cart pose's imageRecordType (individual / group) from its
// product's image nodes: the line's own imageNodes, or the catalog in
// state.shoot.shootProducts. Migration 10 runs it on a persisted cart, and the
// abandoned-cart restore on the cart the server returns, which is installed
// after rehydration and so never passes through the migration.
export const stampPoseImageRecordTypes: Migration = (state) => {
  if (!state || !state.v3Order) return state;

  const v3Order = state.v3Order;
  const orders = v3Order.orders as Record<string, PersistedSlice> | undefined;
  if (!orders) return state;

  type PersistedPose = { imageRecordType?: string } | null | undefined;
  type PersistedChild = { productId?: unknown; poses?: PersistedPose[] };
  type PersistedLine = {
    productId?: unknown;
    isMultiConfig?: boolean;
    imageNodes?: unknown[];
    poses?: PersistedPose[];
    multiConfigChildProducts?: PersistedChild[];
    children?: PersistedChild[];
  };
  type CatalogProduct = {
    id?: unknown;
    image_nodes?: unknown[];
    child_products?: (CatalogProduct | null)[];
  };

  // The persisted catalog (shoot.shootProducts: categories of products),
  // indexed by product id. Ids are unique across price lists, so a line
  // from another event simply finds nothing and is left alone.
  const catalog = new Map<string, CatalogProduct>();
  const index = (product: CatalogProduct | null | undefined) => {
    if (!product || product.id == null) return;
    if (!catalog.has(String(product.id))) {
      catalog.set(String(product.id), product);
    }
    (product.child_products || []).forEach(index);
  };
  const shootProducts = (state.shoot as PersistedSlice | undefined)
    ?.shootProducts;
  if (Array.isArray(shootProducts)) {
    shootProducts.forEach((category) =>
      ((category as { products?: CatalogProduct[] })?.products || []).forEach(
        index,
      ),
    );
  }

  const unstamped = (poses: PersistedPose[] | undefined) =>
    Array.isArray(poses) && poses.some((pose) => pose && !pose.imageRecordType);
  const stamp = (pose: PersistedPose, type: string | undefined) =>
    pose && !pose.imageRecordType && type
      ? { ...pose, imageRecordType: type }
      : pose;

  let changed = false;

  // Pose N of a multi-config line fills child product N's single slot, the
  // rule the add uses; the child comes from the parent product's
  // child_products, or by the child's own id. A child the catalog does not
  // know is left unstamped (the server still refuses a group photo).
  const stampMultiConfig = (line: PersistedLine): PersistedLine => {
    const children = line.multiConfigChildProducts || [];
    if (
      !unstamped(line.poses) &&
      !children.some((child) => unstamped(child?.poses))
    ) {
      return line;
    }

    const parent = catalog.get(String(line.productId));
    const typeAt = (slot: number): string | undefined => {
      const child =
        parent?.child_products?.[slot] ??
        catalog.get(String(children[slot]?.productId));
      if (!child) return undefined;
      return getImageRecords(child)[0]?.type || 'individual';
    };

    let lineChanged = false;
    const stampSlot = (pose: PersistedPose, slot: number) => {
      const next = stamp(pose, typeAt(slot));
      if (next !== pose) lineChanged = true;
      return next;
    };

    const poses = Array.isArray(line.poses)
      ? line.poses.map(stampSlot)
      : line.poses;
    const stampedChildren = children.map((child, slot) =>
      child && Array.isArray(child.poses)
        ? {
            ...child,
            poses: child.poses.map((pose) => stampSlot(pose, slot)),
          }
        : child,
    );
    if (!lineChanged) return line;

    changed = true;
    return {
      ...line,
      poses,
      ...(line.multiConfigChildProducts
        ? { multiConfigChildProducts: stampedChildren }
        : {}),
    };
  };

  // A BYO / You-Pick package keeps each slot's photos on children[i].poses:
  // photo N of child i fills child product i's image node N, the rule the
  // add uses. A child the catalog does not know is left unstamped.
  const stampPackageChildren = (line: PersistedLine): PersistedLine => {
    const children = line.children;
    if (
      !Array.isArray(children) ||
      !children.some((child) => unstamped(child?.poses))
    ) {
      return line;
    }

    let lineChanged = false;
    const stampedChildren = children.map((child) => {
      if (!child || !unstamped(child.poses)) return child;
      const product = catalog.get(String(child.productId));
      if (!product) return child;
      const records = getImageRecords(product);
      lineChanged = true;
      return {
        ...child,
        poses: (child.poses || []).map((pose, slot) =>
          stamp(pose, records[slot]?.type || 'individual'),
        ),
      };
    });
    if (!lineChanged) return line;

    changed = true;
    return { ...line, children: stampedChildren };
  };

  const stampLine = (line: PersistedLine): PersistedLine => {
    if (!line) return line;
    if (line.isMultiConfig) return stampMultiConfig(line);
    if (
      !Array.isArray(line.imageNodes) ||
      !line.imageNodes.length ||
      !unstamped(line.poses)
    ) {
      return stampPackageChildren(line);
    }

    const records = getImageRecords({ image_nodes: line.imageNodes });
    changed = true;
    return stampPackageChildren({
      ...line,
      poses: (line.poses || []).map((pose, slot) =>
        stamp(pose, records[slot]?.type || 'individual'),
      ),
    });
  };

  const stampedOrders = Object.fromEntries(
    Object.entries(orders).map(([orderKey, order]) => {
      const cartItems = order?.cartItems;
      if (!Array.isArray(cartItems)) return [orderKey, order];
      return [orderKey, { ...order, cartItems: cartItems.map(stampLine) }];
    }),
  );

  if (!changed) return state;

  return { ...state, v3Order: { ...v3Order, orders: stampedOrders } };
};

export const migrations: Record<number, Migration> = {
  2: (state) => {
    if (!state) return state;

    // If company.token doesn't exist but company.name does, set token to match name
    if (state.company && state.company.name && !state.company.token) {
      return {
        ...state,
        company: {
          ...state.company,
          token: state.company.name, // Set token to match the existing name value
        },
      };
    }
    return state;
  },
  3: (state) => {
    if (!state) return state;
    const { company, ...rest } = state;
    return rest;
  },
  4: (state) => {
    if (!state) return state;
    const { shoot, ...rest } = state;
    return rest;
  },
  5: (state) => {
    if (!state) return state;
    const { shoot, shootProduct, productConfig, ...rest } = state;

    if (rest.v3Order) {
      return { ...rest, v3Order: { ...rest.v3Order, productConfig: {} } };
    }

    return rest;
  },
  6: (state) => {
    if (!state || !state.additionalData) return state;

    return {
      ...state,
      additionalData: {
        ...state.additionalData,
        picsGalleryId: null,
        picsGalleryPassword: null,
        allAvailablePosesForOrder: [],
        availablePoses: [],
        availableGroupImages: [],
        galleryThatInitiatedOrder: null,
        studentGallery: [],
        studentGroupImages: [],
      },
    };
  },
  7: (state) => {
    if (!state || !state.v3Order) return state;

    const v3Order = state.v3Order;
    const orders = (v3Order.orders as PersistedSlice) || {};
    const hasOrders = Object.keys(orders).length > 0;
    const idsByOrder = v3Order.optionalIncentiveProductIdsToIgnoreByOrder as
      | PersistedSlice
      | undefined;

    if (!hasOrders) {
      return {
        ...state,
        v3Order: {
          ...v3Order,
          ...(idsByOrder
            ? { optionalIncentiveProductIdsToIgnoreByOrder: {} }
            : {}),
          optionalCartLevelIncentiveProductIdsToIgnore: [],
          optionalIncentiveToConfigure: null,
        },
      };
    }

    if (!idsByOrder) return state;

    const prunedIdsByOrder = Object.fromEntries(
      Object.entries(idsByOrder).filter(([orderKey]) =>
        Object.prototype.hasOwnProperty.call(orders, orderKey),
      ),
    );

    return {
      ...state,
      v3Order: {
        ...v3Order,
        optionalIncentiveProductIdsToIgnoreByOrder: prunedIdsByOrder,
      },
    };
  },
  8: (state) => {
    if (!state || !state.v3Order) return state;

    const v3Order = { ...state.v3Order };
    // Ids cannot be translated to SKUs (their catalogs are not in the store),
    // so the id-keyed list is dropped and the SKU-keyed one starts empty.
    delete v3Order.optionalCartLevelIncentiveProductIdsToIgnore;

    return {
      ...state,
      v3Order: {
        ...v3Order,
        optionalCartLevelIncentiveSkusToIgnore: [],
      },
    };
  },
  9: (state) => {
    if (!state || !state.v3Order) return state;

    const v3Order = state.v3Order;
    const productConfig = v3Order.productConfig as PersistedSlice | undefined;
    if (!productConfig) return state;

    // Drop a background from any slot whose product does not take one. Swapping
    // the product in a configured slot used to leave the old product's background
    // behind, and once navigation stopped routing back through the background
    // step for a child that needs none (ENG-2552) nothing overwrote it. The
    // reducer now clears these on the swap, but a session persisted before this
    // deploy rehydrates still carrying one, which the completion gates count as
    // collected and buildCartObject ships to the lab.
    //
    // Only the stale background is removed — the slot, its product, poses and
    // options are all still valid, so this is not a slice drop.
    let changed = false;
    const cleaned = Object.fromEntries(
      Object.entries(productConfig).map(([slotIndex, slot]) => {
        const entry = slot as { product?: unknown; background?: unknown };
        if (
          !entry ||
          entry.background === undefined ||
          productNeedsBackgroundSelection(entry.product as ProductLike)
        ) {
          return [slotIndex, slot];
        }

        changed = true;
        const { background, ...rest } = entry;
        return [slotIndex, rest];
      }),
    );

    if (!changed) return state;

    return { ...state, v3Order: { ...v3Order, productConfig: cleaned } };
  },
  10: stampPoseImageRecordTypes,
};

// Must match the highest key in `migrations` above — asserted by the spec.
export const PERSIST_VERSION = 10;
